import React from "react";
import { Chip } from "@mui/material";
import { css } from "@emotion/react";
import { styled } from "@mui/material/styles";

type Props = {
  selected: any[];
  items: { value: string | number; displayName: string }[];
};

const FormikMultiSelectValueRenderer: React.FC<Props> = ({ items, selected }) => {
  const selectedItems = React.useMemo(() => items.filter((option) => selected.includes(option.value)), [
    items,
    selected,
  ]);

  const [hiddenCount, setHiddenCount] = React.useState(0);

  const containerRef = React.useRef<HTMLDivElement>(null);
  const chipRefs = React.useRef<{ [key: string]: HTMLDivElement | null }>({});
  const ellipsisRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!containerRef.current || !ellipsisRef.current) {
      return;
    }

    const computedStyle = window.getComputedStyle(containerRef.current);
    const gap = computedStyle.gap;

    const chipSpacing = parseFloat(gap);
    const containerWidth = containerRef.current.clientWidth;
    const ellipsisWidth = ellipsisRef.current.clientWidth;

    let totalWidth: number = 0,
      lastIndexVisible: number = 0;
    for (let index = 0; index < selectedItems.length; index++) {
      const selectedItem = selectedItems[index];
      const chipWidth = chipRefs.current[selectedItem.value]?.clientWidth || 0;

      // Determine whether the current Chip can fit the container
      totalWidth += chipWidth + chipSpacing;
      if (totalWidth + ellipsisWidth > containerWidth) {
        break;
      }

      lastIndexVisible = index;
    }

    setHiddenCount(selectedItems.length - (lastIndexVisible + 1));
  }, [selectedItems]);

  return (
    <ChipList ref={containerRef}>
      {selectedItems.map(({ displayName, value }, index) => (
        <StyledChip
          ref={(el) => (chipRefs.current[value] = el)}
          key={value}
          label={displayName}
          variant={"outlined"}
          color={"blue"}
          $isHidden={index >= selectedItems.length - hiddenCount}
        />
      ))}

      <StyledChip
        ref={ellipsisRef}
        $isHidden={hiddenCount === 0}
        label={`+${hiddenCount} more`}
        variant={"outlined"}
        color={"default"}
      />
    </ChipList>
  );
};

const StyledChip = styled(Chip, { shouldForwardProp: (prop) => prop !== "$isHidden" })<{ $isHidden?: boolean }>(
  ({ theme, $isHidden }) => css`
    ${$isHidden &&
    css`
      position: absolute;
      visibility: hidden;
    `}
  `
);

const ChipList = styled("div")(
  ({ theme }) => css`
    display: flex;
    flex-direction: row;
    ${theme.mixins.flexGap(theme.spacing(2))}
  `
);
export default FormikMultiSelectValueRenderer;
